import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { InspectorPanel } from 'components/wysiwyg/InspectorPanel/InspectorPanel';
import { TemplatesSidebar } from 'components/wysiwyg/TemplatesSidebar';
import { EditorToolbar } from 'components/wysiwyg/EditorToolbar';
import { PreviewArea } from 'components/wysiwyg/PreviewArea';
import { DataScenario, RenderRequest, Variant, VariantWithTemplate } from 'types/cx-editor';
import { createCxApiClient } from 'framework/http/cxEditorClient';
import { useGlobalConfig } from 'context/GlobalContext';
import { WidgetSdkProvider } from 'context/WidgetSdkContext';
import { WidgetSdkErrorBoundary } from 'context/WidgetSdkErrorBoundary';
import { WidgetErrorBoundary } from 'components/wysiwyg/WidgetErrorBoundary';
import CompareDialog from 'components/wysiwyg/CompareDialog';
import { toast } from 'utils/toast';
import { Box, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button } from '@mui/material';
import { resolveScenarioId } from 'utils/cx-editor';
import { useSelector, useDispatch } from 'react-redux';
import { selectSelectedPilotId, selectPilotList } from 'store/dataReducers/pilotsSlice';
import { selectProjectAttributes } from 'store/dataReducers/applicationScopeSlice';
import { 
  selectLevel1Data, 
  selectLevel2Data, 
  selectLastFetchedPilotId,
  setLevel1Data, 
  setLevel2Data, 
  setLoading, 
  setError, 
  setLastFetchedPilotId 
} from 'store/dataReducers/featureMetricsSlice';
import {
  setDataScenarios,
  setDataScenariosLoading,
  setDataScenariosError,
} from 'store/dataReducers/dataScenariosSlice';

const DEFAULT_VARIANT: Variant = { hierarchy: 'Project', locale: 'en_US', scenario: 'default' };

// Module-level in-flight tracking to prevent duplicate API calls across component instances and StrictMode re-renders
const inFlightTemplateMeta = new Set<string>(); // Track template meta requests in progress


export const WysiwygPage: React.FC = () => {
  const { rc } = useGlobalConfig();
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedPilotId = useSelector(selectSelectedPilotId);
  const pilotList = useSelector(selectPilotList);
  const projectAttributes = useSelector(selectProjectAttributes);
  const lastFetchedPilotId = useSelector(selectLastFetchedPilotId);
  const level1Data = useSelector(selectLevel1Data);

  // Read URL params for state restoration (when navigating from EditColorPalettePage)
  const templateKeyParam = searchParams.get('templateKey');
  const elementKeyParam = searchParams.get('elementKey');
  const localeParam = searchParams.get('locale');
  const scenarioParam = searchParams.get('scenario');

  // Get environment and pilotId from Redux state
  const selectedPilot = React.useMemo(() => {
    if (!selectedPilotId || !pilotList.length) return null;
    return pilotList.find((pilot: any) => String(pilot.projectId) === String(selectedPilotId));
  }, [selectedPilotId, pilotList]);

  // SDK loading is handled by WidgetSdkProvider wrapper
  // No need to load SDK here to avoid duplicate loading attempts
  const [templateKey, setTemplateKey] = React.useState<string>(templateKeyParam || '');
  const [elementKey, setElementKey] = React.useState<string>(elementKeyParam || 'Full Template');
  const [variant, setVariant] = React.useState<Variant>({
    ...DEFAULT_VARIANT,
    locale: localeParam || DEFAULT_VARIANT.locale,
    scenario: scenarioParam || DEFAULT_VARIANT.scenario,
  });
  const [values, setValues] = React.useState<Record<string, any>>({});
  const [renderedHtml, setRenderedHtml] = React.useState<string>(''); // Store rendered HTML for technical section ID lookup
  const [compareOpen, setCompareOpen] = React.useState(false);
  const [variantB, setVariantB] = React.useState<VariantWithTemplate | null>(null);
  const [availableScenarios, setAvailableScenarios] = React.useState<Array<{ value: string; label: string }>>([]);
  const [scenariosLoading, setScenariosLoading] = React.useState(false);
  const [availableElements, setAvailableElements] = React.useState<Array<{ elementKey: string; displayName: string }>>([
    { elementKey: 'Full Template', displayName: 'Full Template' }
  ]); // Elements parsed from rendered HTML
  const [sidebarOpen, setSidebarOpen] = React.useState(true);
  const [inspectorOpen, setInspectorOpen] = React.useState(true);
  const [previewMode, setPreviewMode] = React.useState<'desktop' | 'mobile'>('desktop');
  const inspectorSaveRef = React.useRef<(() => Promise<void>) | null>(null); // Store InspectorPanel save function
  const inspectorCancelRef = React.useRef<(() => void) | null>(null); // Store InspectorPanel cancel function
  const [modifiedCount, setModifiedCount] = React.useState<number>(0); // Track number of modified fields
  const [refreshKey, setRefreshKey] = React.useState<number>(0); // Refresh trigger to force preview re-render after save
  const [previewBusy, setPreviewBusy] = React.useState(false); // Disable template/section/scenario changes while preview is rendering
  const didInitTemplateRef = React.useRef(false);
  
  // Clear URL params after state restoration (to keep URL clean)
  React.useEffect(() => {
    if (templateKeyParam || elementKeyParam || localeParam || scenarioParam) {
      setSearchParams({}, { replace: true });
    }
  }, []); 

  // Confirmation dialog state for unsaved changes
  const [unsavedChangesDialog, setUnsavedChangesDialog] = React.useState<{
    open: boolean;
    pendingAction: 'template' | 'element' | 'scenario';
    pendingValue: string | Variant;
  }>({
    open: false,
    pendingAction: 'template',
    pendingValue: '',
  });

  // Fetch Level 1 and Level 2 feature metrics on mount or when pilotId changes
  React.useEffect(() => {
    if (!selectedPilotId) {
      return;
    }

    // Skip if we already have data for this pilot
    if (lastFetchedPilotId === String(selectedPilotId)) {
      return;
    }

    let ignore = false;

    async function fetchFeatureMetrics() {
      dispatch(setLoading(true));
      dispatch(setError(null));

      try {
        const api = createCxApiClient(rc, process.env.REACT_APP_TOOL_API_URL as string);
        const pilotIdStr = String(selectedPilotId);

        console.log('[WysiwygPage] Fetching Level 1 and Level 2 feature metrics for pilot:', pilotIdStr);

        // Fetch both APIs in parallel
        const [level1Response, level2Response] = await Promise.all([
          api.getFeatureMetrics(pilotIdStr, 'LEVEL_1'),
          api.getFeatureMetrics(pilotIdStr, 'LEVEL_2')
        ]);

        if (!ignore) {
          dispatch(setLevel1Data(level1Response));
          dispatch(setLevel2Data(level2Response));
          dispatch(setLastFetchedPilotId(pilotIdStr));
          dispatch(setLoading(false));
          console.log('[WysiwygPage] Feature metrics loaded successfully', {
            level1Count: level1Response.length,
            level2Count: level2Response.length
          });
        }
      } catch (err: any) {
        if (!ignore) {
          console.error('[WysiwygPage] Failed to fetch feature metrics:', err);
          dispatch(setError(err.message || 'Failed to load feature metrics'));
          dispatch(setLoading(false));
        }
      }
    }

    fetchFeatureMetrics();

    return () => {
      ignore = true;
    };
  }, [selectedPilotId, rc, dispatch, lastFetchedPilotId]);

  // Extract fuel_type from projectAttributes (similar to InspectorPanel)
  const fuelType = React.useMemo(() => {
    let fuelType = 'ELECTRIC'; // Default fallback
    if (projectAttributes?.fuel_type) {
      // Handle different formats: string, array, or comma-separated
      if (typeof projectAttributes.fuel_type === 'string') {
        // If comma-separated, take the first one
        const fuelTypes = projectAttributes.fuel_type.split(',').map(f => f.trim());
        fuelType = fuelTypes[0]?.toUpperCase() || 'ELECTRIC';
      } else if (Array.isArray(projectAttributes.fuel_type)) {
        const fuelTypeArray = projectAttributes.fuel_type as any[];
        if (fuelTypeArray.length > 0) {
          fuelType = String(fuelTypeArray[0]).toUpperCase();
        }
      }
    }
    return fuelType;
  }, [projectAttributes]);

  // Initialize variantA when templateKey or variant changes
  // Use useMemo to prevent unnecessary object recreation
  const variantA = React.useMemo(() => {
    if (!templateKey) return null;
    return {
      templateKey,
      ...variant,
    };
  }, [templateKey, variant.hierarchy, variant.locale, variant.scenario]);

  // Scenarios are now loaded by EditorToolbar and passed via callback to avoid duplicate API calls
  // This useEffect is kept as fallback but will be populated by EditorToolbar's onScenariosLoaded callback
  const handleScenariosLoaded = React.useCallback((scenarios: Array<{ label: string; value: string }>) => {
    console.log('[WysiwygPage] Scenarios received from EditorToolbar:', scenarios);
    setAvailableScenarios(scenarios);
  }, []);
  
  // Track if we should fetch template meta after render completes (for optimization)
  const [shouldFetchTemplateMeta, setShouldFetchTemplateMeta] = React.useState(false);
  const lastFetchedTemplateKeyRef = React.useRef<string>('');
  
  // Handle render completion - trigger template/scenario fetch (optimization, not required)
  const handleRenderComplete = React.useCallback((completedTemplateKey: string) => {
    console.log('[WysiwygPage] Render complete for template:', completedTemplateKey);
    // Only trigger if this is a new template (not already fetched)
    if (completedTemplateKey && completedTemplateKey !== lastFetchedTemplateKeyRef.current) {
      setShouldFetchTemplateMeta(true);
    }
  }, []);
  
  // Determine channel from templateKey for PreviewArea
  const channel = React.useMemo(() => {
    if (!templateKey) return 'Email';
    if (templateKey.startsWith('WEB_') || templateKey.startsWith('DASHBOARD.')) return 'Web';
    if (templateKey === 'HER') return 'HER';
    if (templateKey.startsWith('SMS.')) return 'Email'; // SMS maps to Email channel type
    return 'Email';
  }, [templateKey]);

  // When switching templates (product type/email type), always reset section to "Full Template"
  // so the new selection starts from a predictable default.
  // Skip the first run to avoid clobbering URL-param state restoration.
  React.useEffect(() => {
    if (!didInitTemplateRef.current) {
      didInitTemplateRef.current = true;
      return;
    }
    setElementKey('Full Template');
    setAvailableElements([{ elementKey: 'Full Template', displayName: 'Full Template' }]);
    setRenderedHtml('');
    setVariant(prev => ({ ...prev, scenario: 'default' }));
  }, [templateKey]);

  // Fetch scenarios independently when templateKey changes (not dependent on render success)
  // This ensures scenarios are always available even if render fails
  React.useEffect(() => {
    if (!templateKey || !selectedPilotId) {
      return;
    }

    const requestKey = `scenarios-${templateKey}-${selectedPilotId}`;

    if (inFlightTemplateMeta.has(requestKey)) {
      console.log('[WysiwygPage] Scenarios request already in flight, skipping duplicate:', requestKey);
      return;
    }

    inFlightTemplateMeta.add(requestKey);

    let ignore = false;
    async function loadScenarios() {
      const locale = variant.locale || 'en_US';
      const pilotId = String(selectedPilotId);
      
      // Set loading state in Redux
      dispatch(setDataScenariosLoading({ pilotId, fuelType, locale, loading: true }));
      setScenariosLoading(true);
      
      try {
        const api = createCxApiClient(rc, process.env.REACT_APP_TOOL_API_URL as string);
        console.log('[WysiwygPage] Fetching data scenarios for template:', {
          templateKey,
          selectedPilotId,
          fuelType,
          locale
        });

        const dataScenariosResp = await api.getDataScenarios(
          pilotId, 
          fuelType, 
          locale
        );

        if (ignore) {
          return;
        }

        inFlightTemplateMeta.delete(requestKey);

        const dataScenarios = dataScenariosResp?.dataScenarios as DataScenario[] | undefined;
        
        // Store full data scenarios array in Redux
        if (dataScenarios && dataScenarios.length > 0) {
          dispatch(setDataScenarios({
            pilotId,
            fuelType,
            locale,
            dataScenarios,
          }));
        }

        // Also compute filtered scenarios for backward compatibility with existing code
        let scenarioOptions: Array<{ label: string; value: string }> = [];
        if (dataScenarios?.length) {
          scenarioOptions = dataScenarios
            .filter((ds: DataScenario) => {
              const matchesTemplate = ds.templateName === templateKey;
              const matchesProduct = (ds.productType || '').toLowerCase() === (channel || 'Email').toLowerCase();
              return matchesTemplate && matchesProduct;
            })
            .map((ds: DataScenario) => ({
              label: ds.scenarioDisplayName || ds.scenarioId,
              value: ds.scenarioId
            }));

          scenarioOptions.sort((a, b) => {
            if (a.value === b.value) return 0;
            const leftDefault = dataScenarios.find((ds: DataScenario) => ds.scenarioId === a.value)?.defaultScenario;
            const rightDefault = dataScenarios.find((ds: DataScenario) => ds.scenarioId === b.value)?.defaultScenario;
            if (leftDefault && !rightDefault) return -1;
            if (!leftDefault && rightDefault) return 1;
            return a.label.localeCompare(b.label);
          });
        }

        if (scenarioOptions.length === 0) {
          scenarioOptions = [{ label: 'Default', value: 'default' }];
        }

        setAvailableScenarios(scenarioOptions);
        handleScenariosLoaded(scenarioOptions);
      } catch (error: any) {
        if (!ignore) {
          console.error('[WysiwygPage] Failed to load data scenarios:', error);
          inFlightTemplateMeta.delete(requestKey);
          
          // Set error state in Redux
          dispatch(setDataScenariosError({
            pilotId: String(selectedPilotId),
            fuelType,
            locale: variant.locale || 'en_US',
            error: error?.message || 'Failed to load data scenarios',
          }));
          
          // Even on error, show at least a default option so dropdown is usable
          const fallback = [{ label: 'Default', value: 'default' }];
          setAvailableScenarios(fallback);
          handleScenariosLoaded(fallback);
        }
      } finally {
        if (!ignore) {
          const locale = variant.locale || 'en_US';
          dispatch(setDataScenariosLoading({
            pilotId: String(selectedPilotId),
            fuelType,
            locale,
            loading: false,
          }));
          setScenariosLoading(false);
        }
      }
    }
    loadScenarios();
    return () => {
      ignore = true;
    };
  }, [
    templateKey,
    selectedPilotId,
    fuelType,
    variant.locale,
    channel,
    rc,
    handleScenariosLoaded
  ]);

  // If no explicit scenario is chosen yet (variant.scenario === "default"),
  // align it to the first scenario option we have for this template.
  // This prevents PreviewArea from calling render with scenarioId="default" before the toolbar runs.
  React.useEffect(() => {
    if (!templateKey) return;
    if (variant.scenario !== 'default') return;
    if (!availableScenarios || availableScenarios.length === 0) return;

    const first = availableScenarios[0]?.value;
    if (!first || first === 'default') return;

    setVariant(prev => ({ ...prev, scenario: first }));
  }, [templateKey, channel, availableScenarios, variant.scenario, variant.locale]);

  // Fetch template meta AFTER render completes (for optimization - elements from HTML parsing)
  // This is separate from scenarios fetch to allow scenarios to show even if render fails
  React.useEffect(() => {
    if (!shouldFetchTemplateMeta || !templateKey) {
      return;
    }

    const requestKey = `${templateKey}-${elementKey || ''}`;

    if (inFlightTemplateMeta.has(requestKey)) {
      console.log('[WysiwygPage] Template meta request already in flight, skipping duplicate:', requestKey);
      return;
    }

    inFlightTemplateMeta.add(requestKey);
    lastFetchedTemplateKeyRef.current = templateKey;

    let ignore = false;
    async function loadTemplateMeta() {
      try {
        const api = createCxApiClient(rc, process.env.REACT_APP_TOOL_API_URL as string);
        console.log('[WysiwygPage] Fetching template meta:', {
          templateKey,
          elementKey,
          requestKey
        });

        const meta = await api.getTemplateMeta(templateKey, elementKey);

        if (ignore) {
          return;
        }

        inFlightTemplateMeta.delete(requestKey);

        if (!availableElements || availableElements.length === 0) {
          setAvailableElements(meta.elements || []);
        }

        setShouldFetchTemplateMeta(false);
      } catch (error: any) {
        if (!ignore) {
          console.error('[WysiwygPage] Failed to load template meta:', error);
          inFlightTemplateMeta.delete(requestKey);
          setShouldFetchTemplateMeta(false);
        }
      }
    }
    loadTemplateMeta();
    return () => {
      ignore = true;
    };
  }, [
    shouldFetchTemplateMeta,
    templateKey,
    elementKey,
    rc,
    availableElements
  ]);

  // Wrapper functions to intercept changes and check for unsaved changes
  const handleTemplateChange = React.useCallback((newTemplateKey: string) => {
    if (modifiedCount > 0 && newTemplateKey !== templateKey) {
      // Show confirmation dialog
      setUnsavedChangesDialog({
        open: true,
        pendingAction: 'template',
        pendingValue: newTemplateKey,
      });
    } else {
      // No unsaved changes, proceed with change
      setTemplateKey(newTemplateKey);
    }
  }, [modifiedCount, templateKey]);

  const handleElementChange = React.useCallback((newElementKey: string) => {
    if (modifiedCount > 0 && newElementKey !== elementKey) {
      // Show confirmation dialog
      setUnsavedChangesDialog({
        open: true,
        pendingAction: 'element',
        pendingValue: newElementKey,
      });
    } else {
      // No unsaved changes, proceed with change
      setElementKey(newElementKey);
    }
  }, [modifiedCount, elementKey]);

  const handleVariantChangeWithConfirmation = React.useCallback((newVariant: Variant) => {
    // Check if scenario is changing (which triggers re-rendering)
    const scenarioChanged = newVariant.scenario !== variant.scenario;
    
    // Only show dialog if scenario changes (as it triggers re-rendering)
    // Hierarchy and locale changes might not trigger re-rendering, but scenario definitely does
    if (modifiedCount > 0 && scenarioChanged) {
      // Show confirmation dialog
      setUnsavedChangesDialog({
        open: true,
        pendingAction: 'scenario',
        pendingValue: newVariant,
      });
    } else {
      // No unsaved changes or scenario not changing, proceed with change
      setVariant(newVariant);
    }
  }, [modifiedCount, variant]);

  // Handle confirmation dialog actions
  const handleSaveAndContinue = React.useCallback(async () => {
    const { pendingAction, pendingValue } = unsavedChangesDialog;
    // Save changes first - call the actual save function
    if (inspectorSaveRef.current) {
      try {
        await inspectorSaveRef.current();
        // After save, proceed with the pending change
        if (pendingAction === 'template') {
          setTemplateKey(pendingValue as string);
        } else if (pendingAction === 'element') {
          setElementKey(pendingValue as string);
        } else if (pendingAction === 'scenario') {
          setVariant(pendingValue as Variant);
        }
        setUnsavedChangesDialog({ open: false, pendingAction: 'template', pendingValue: '' });
      } catch (error) {
        console.error('[WysiwygPage] Error saving before switch:', error);
        toast.error('Failed to save changes. Please try again.');
      }
    } else {
      // Fallback: if no save function available, just proceed with the change
      console.warn('[WysiwygPage] No save function available, proceeding with change');
      if (pendingAction === 'template') {
        setTemplateKey(pendingValue as string);
      } else if (pendingAction === 'element') {
        setElementKey(pendingValue as string);
      } else if (pendingAction === 'scenario') {
        setVariant(pendingValue as Variant);
      }
      setUnsavedChangesDialog({ open: false, pendingAction: 'template', pendingValue: '' });
    }
  }, [unsavedChangesDialog]);

  const handleDiscardAndContinue = React.useCallback(() => {
    const { pendingAction, pendingValue } = unsavedChangesDialog;
    // Discard changes and proceed with the pending change
    if (inspectorCancelRef.current) {
      inspectorCancelRef.current();
    }
    if (pendingAction === 'template') {
      setTemplateKey(pendingValue as string);
    } else if (pendingAction === 'element') {
      setElementKey(pendingValue as string);
    } else if (pendingAction === 'scenario') {
      setVariant(pendingValue as Variant);
    }
    setUnsavedChangesDialog({ open: false, pendingAction: 'template', pendingValue: '' });
  }, [unsavedChangesDialog]);

  const handleCancelSwitch = React.useCallback(() => {
    // Cancel the switch, keep current state
    setUnsavedChangesDialog({ open: false, pendingAction: 'template', pendingValue: '' });
  }, []);

  // Auto-collapse both TemplatesSidebar and InspectorPanel when Web channel is selected (for larger preview)
  // Collapse on any Web template/element selection or change, not just the first one
  const prevWebSelectionRef = React.useRef<string>(''); // Track previous Web template/element combination
  React.useEffect(() => {
    // If we're on Web channel and have a template selected
    if (channel === 'Web' && templateKey) {
      const currentSelection = `${templateKey}-${elementKey}`;
      
      // Collapse both sidebars whenever a Web item is selected/changed
      // This includes: first Web item, switching between Web items, or any Web-related change
      if (currentSelection !== prevWebSelectionRef.current) {
        // Collapse both sidebars if they are open
        if (sidebarOpen) {
          setSidebarOpen(false);
        }
        if (inspectorOpen) {
          setInspectorOpen(false);
        }
        prevWebSelectionRef.current = currentSelection;
      }
    } else if (channel !== 'Web') {
      // Reset tracking when not on Web channel
      prevWebSelectionRef.current = '';
    }
  }, [channel, templateKey, elementKey]); // Track templateKey and elementKey to collapse on any Web item change

  // Prepare available templates list from Redux Level 1 data
  const availableTemplates = React.useMemo(() => {
    if (!level1Data) return [];
    
    // Filter only ACCESS resolutions and extract template info
    return level1Data
      .filter(r => r.resolution === 'ACCESS')
      .map(r => {
        // Extract display name from featureId
        const parts = r.featureId.split('.');
        const lastPart = parts[parts.length - 1] || r.featureId;
        const displayName = lastPart
          .split('_')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
          .join(' ');
        
        return {
          templateKey: r.featureId,
          name: displayName
        };
      });
  }, [level1Data]);

  // Memoize renderReq to prevent unnecessary re-renders in PreviewArea
  // Include refreshKey to force re-render after save while preserving elementKey and scenario
  const renderReq: RenderRequest | null = React.useMemo(() => {
    return variantA
      ? { templateKey: variantA.templateKey, elementKey, variant: { hierarchy: variantA.hierarchy, locale: variantA.locale, scenario: variantA.scenario }, previewMode: 'desktop', values, refreshKey }
      : null;
  }, [variantA?.templateKey, variantA?.hierarchy, variantA?.locale, variantA?.scenario, elementKey, values, refreshKey]);
  
  const renderReqB: RenderRequest | null = React.useMemo(() => {
    return variantB
      ? { templateKey: variantB.templateKey, elementKey, variant: { hierarchy: variantB.hierarchy, locale: variantB.locale, scenario: variantB.scenario }, previewMode: 'desktop', values }
      : null;
  }, [variantB?.templateKey, variantB?.hierarchy, variantB?.locale, variantB?.scenario, elementKey, values]);

  const handleVariantChange = (variant: 'A' | 'B', data: VariantWithTemplate) => {
    if (variant === 'A') {
      // Update the main templateKey and variant - variantA will be recalculated via useMemo
      if (data.templateKey !== templateKey) {
        setTemplateKey(data.templateKey);
      }
      setVariant({ hierarchy: data.hierarchy, locale: data.locale, scenario: data.scenario });
    } else {
      setVariantB(data);
    }
  };

  const handleCompareRun = () => {
    // Both variants are already set via onChange, just close the dialog
    // The renderReq and renderReqB will automatically update
  };

  return (
    <WidgetErrorBoundary>
      <WidgetSdkErrorBoundary>
        <WidgetSdkProvider>
        <Box
        sx={{
          display: 'flex',
          height: 'calc(100vh - 8rem)',
          gap: 0,
          overflow: 'hidden'
        }}
      >
        <Box
          sx={{
            width: sidebarOpen ? 300 : 48,
            minWidth: sidebarOpen ? 300 : 48,
            transition: 'width 300ms ease-in-out',
            flexShrink: 0,
            overflow: 'hidden'
          }}
        >
          <TemplatesSidebar
            onSelect={handleTemplateChange}
            selectedTemplateKey={templateKey}
            isOpen={sidebarOpen}
            onToggleOpen={() => setSidebarOpen(o => !o)}
            disabled={previewBusy}
          />
        </Box>

        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            minWidth: 0,
            pl: 2,
            gap: 2,
            overflow: 'hidden'
          }}
        >
          {/* EditorToolbar Upper Section - Full Width */}
          <Box sx={{ width: '100%' }}>
            <EditorToolbar
              templateKey={templateKey}
              variant={variant}
              channel={channel}
              previewMode={previewMode}
              onVariantChange={handleVariantChangeWithConfirmation}
              elementKey={elementKey}
              onElementChange={handleElementChange}
              disabled={previewBusy}
              onPreviewModeChange={setPreviewMode}
              onOpenCompare={() => setCompareOpen(true)}
              onCancel={() => {
                // Call InspectorPanel cancel function if available
                if (inspectorCancelRef.current) {
                  inspectorCancelRef.current();
                }
              }}
              onSave={async () => {
                // Call InspectorPanel save function if available
                if (inspectorSaveRef.current) {
                  await inspectorSaveRef.current();
                } else {
                  // Fallback to old save if InspectorPanel save not available
                  if (!templateKey) return;
                  try {
                    const api = createCxApiClient(rc, process.env.REACT_APP_TOOL_API_URL as string);
                    
                    // Resolve "default" scenario to actual scenario ID from data scenarios API
                    const resolvedScenario = await resolveScenarioId(
                      variant.scenario || 'default',
                      templateKey,
                      channel || 'Email',
                      selectedPilotId || '',
                      fuelType,
                      variant.locale || 'en_US',
                      (pilot_id, fuel_type, locale) => api.getDataScenarios(pilot_id, fuel_type, locale)
                    );
                    
                    const resp = await api.saveChanges({ 
                      templateKey, 
                      elementKey, 
                      variant: { ...variant, scenario: resolvedScenario }, 
                      changes: { text: [], colors: [], images: [], metadata: [] }, 
                      message: 'Quick save' 
                    });
                    toast.success(`Saved (version ${resp.versionId})`);
                  } catch (e: any) {
                    toast.error('Save failed');
                  }
                }
              }}
              renderOnlyUpperSection={true}
              availableElements={availableElements}
              onRenderComplete={handleRenderComplete}
              modifiedCount={modifiedCount}
            />
          </Box>

          {/* Flex Row: Left side (Lower Section + PreviewArea stacked), Right side (InspectorPanel) */}
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'row',
              gap: 2,
              minHeight: 0,
              overflow: 'hidden'
            }}
          >
            {/* Left side: Lower Section and PreviewArea stacked vertically */}
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                minWidth: 0,
                minHeight: 0,
                gap: 2,
                overflow: 'hidden'
              }}
            >
              {/* EditorToolbar Lower Section */}
              <Box sx={{ flexShrink: 0 }}>
                <EditorToolbar
                  templateKey={templateKey}
                  variant={variant}
                  channel={channel}
                  previewMode={previewMode}
                  onVariantChange={handleVariantChangeWithConfirmation}
                  elementKey={elementKey}
                  onElementChange={handleElementChange}
                  onPreviewModeChange={setPreviewMode}
                  onOpenCompare={() => setCompareOpen(true)}
                  onCancel={() => {
                    // Call InspectorPanel cancel function if available
                    if (inspectorCancelRef.current) {
                      inspectorCancelRef.current();
                    }
                  }}
                  onSave={async () => {
                    // Call InspectorPanel save function if available
                    if (inspectorSaveRef.current) {
                      await inspectorSaveRef.current();
                    } else {
                      // Fallback to old save if InspectorPanel save not available
                      if (!templateKey) return;
                      try {
                        const api = createCxApiClient(rc, process.env.REACT_APP_TOOL_API_URL as string);
                        const resp = await api.saveChanges({ templateKey, elementKey, variant, changes: { text: [], colors: [], images: [], metadata: [] }, message: 'Quick save' });
                        toast.success(`Saved (version ${resp.versionId})`);
                      } catch (e: any) {
                        toast.error('Save failed');
                      }
                    }
                  }}
                  renderOnlyLowerSection={true}
                  availableElements={availableElements}
                  onRenderComplete={handleRenderComplete}
                  modifiedCount={modifiedCount}
                />
              </Box>

              {/* PreviewArea */}
              <Box
                sx={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  minWidth: 0,
                  minHeight: 0,
                  overflow: 'hidden'
                }}
              >
                <PreviewArea 
                  request={renderReq} 
                  rightRequest={renderReqB}
                  previewMode={previewMode}
                  channel={channel}
                  isFullTemplateSelected={elementKey === 'Full Template'}
                  elementKey={elementKey}
                  variant={variant}
                  onLoadingChange={setPreviewBusy}
                  onSectionsParsed={(sections) => {
                    // Update available elements from parsed HTML sections
                    setAvailableElements(sections);
                    // If current elementKey is not in the new list, reset to Full Template
                    if (!sections.find(s => s.elementKey === elementKey)) {
                      setElementKey('Full Template');
                    }
                  }}
                  onRenderComplete={handleRenderComplete}
                  onHtmlChange={(html) => {
                    setRenderedHtml(html);
                  }}
                />
              </Box>
            </Box>

            {/* Right side: InspectorPanel */}
            <Box
              sx={{
                width: inspectorOpen ? 400 : 48,
                minWidth: inspectorOpen ? 400 : 48,
                transition: 'width 300ms ease-in-out',
                flexShrink: 0,
                overflow: 'hidden'
              }}
            >
              <InspectorPanel
                templateKey={templateKey}
                elementKey={elementKey}
                variant={variant}
                onValuesChange={setValues}
                isOpen={inspectorOpen}
                onToggleOpen={() => setInspectorOpen(o => !o)}
                renderedHtml={renderedHtml}
                refreshKey={refreshKey}
                onSaveReady={(saveFn) => {
                  inspectorSaveRef.current = saveFn;
                }}
                onCancelReady={(cancelFn) => {
                  inspectorCancelRef.current = cancelFn;
                }}
                onModifiedCountChange={(count) => {
                  setModifiedCount(count);
                }}
                onSaveSuccess={() => {
                  // Increment refreshKey to trigger preview re-render after save
                  // This preserves the current elementKey and scenario
                  setRefreshKey(prev => prev + 1);
                }}
              />
            </Box>
          </Box>
        </Box>
      </Box>

      <CompareDialog
        open={compareOpen}
        onOpenChange={setCompareOpen}
        variantA={variantA}
        variantB={variantB}
        onChange={handleVariantChange}
        onRun={handleCompareRun}
        availableTemplates={availableTemplates}
        availableScenarios={availableScenarios}
        availableClusters={[]}
      />

      {/* Unsaved Changes Confirmation Dialog */}
      <Dialog 
        open={unsavedChangesDialog.open} 
        onClose={handleCancelSwitch}
        aria-labelledby="unsaved-changes-dialog-title"
        aria-describedby="unsaved-changes-dialog-description"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle id="unsaved-changes-dialog-title">
          Unsaved Changes
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="unsaved-changes-dialog-description">
            You have {modifiedCount} unsaved {modifiedCount === 1 ? 'change' : 'changes'}. 
            What would you like to do before switching?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelSwitch}>
            Cancel
          </Button>
          <Button onClick={handleDiscardAndContinue} color="error" variant="outlined">
            Discard Changes
          </Button>
          <Button onClick={handleSaveAndContinue} color="primary" variant="contained" autoFocus>
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>
        </WidgetSdkProvider>
      </WidgetSdkErrorBoundary>
    </WidgetErrorBoundary>
  );
}

export default WysiwygPage;


